/**
 * Support at Home Problem Identifier
 *
 * Analyzes baseline financial position to identify optimization opportunities
 * and determine which strategies are relevant for the client's situation.
 * This enables intelligent filtering to avoid wasting time on strategies that won't help.
 */
import { anyoneOwnsHome } from '../../../shared/src/calculation/householdPosition.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { resolveServicePlanForSuggestionEngine, type SupportAtHomeServicePlan, type ResolvedServicePlan } from './supportAtHomeEstimatedServicePlan.ts';
import {
    getProjectionCashFlow,
    getAnnualContributionAmount,
    type SupportAtHomeProjectionYear,
    type SupportAtHomeContributions,
} from './supportAtHomeProjectionUtils.ts';
import {
    PENSION_RATES,
    ASSET_TEST_LIMITS,
    ANNUAL_REDUCTION_PER_1000_ASSETS,
} from '../constants/index.ts';
import * as calculationApi from '../services/calculationApi.ts';
import { formatCurrency } from '../utils/index.ts';
import { FUNERAL_BOND_LIMIT } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';

export interface PensionOptimizationOpportunity {
    status: 'full' | 'part' | 'self-funded';
    currentPension: number;
    maxPension: number;
    pensionRatio: number;
    canImprovePension: boolean;
    assetsAboveThreshold: number;
    estimatedMaxPensionGain: number;
    assessableAssets: number;
    assetThreshold: number;
}

export interface ContributionOptimizationOpportunity {
    currentRates: { clinical: number; independence: number; everydayLiving: number };
    annualContribution: number;
    canReduceViaPension: boolean;
    ratesAtMinimum: boolean;
    optimizationPotential: 'high' | 'medium' | 'low';
}

export interface CashflowOpportunity {
    year1Cashflow: number;
    yearsToDeficit: number | null;
    cashflowOptimizationNeeded: boolean;
    averageCashflow: number;
    allYearsPositive?: boolean;
}

export type RecommendedFocus = 'pension_and_contributions' | 'pension_and_cashflow' | 'cashflow' | 'legacy' | 'balanced';

export interface StrategyRelevanceEntry {
    relevant: boolean;
    reason: string;
}

export interface StrategyRelevance {
    gifting: StrategyRelevanceEntry;
    funeralBonds: StrategyRelevanceEntry;
    annuities: StrategyRelevanceEntry;
    propertyStrategies: StrategyRelevanceEntry;
    agedCareIntegration: StrategyRelevanceEntry;
}

export interface ProblemStatement {
    summary: string;
    pensionOptimization: {
        status: PensionOptimizationOpportunity['status'];
        canImprove: boolean;
        currentPension: number;
        maxPension: number;
        estimatedMaxGain: number;
        reason: string;
        recommendation: string;
    };
    contributionOptimization: {
        currentRates: ContributionOptimizationOpportunity['currentRates'];
        annualContribution: number;
        canReduceViaPension: boolean;
        ratesAtMinimum: boolean;
        optimizationPotential: ContributionOptimizationOpportunity['optimizationPotential'];
    };
    cashflowPosition: {
        year1Cashflow: number;
        yearsToDeficit: number | null;
        optimizationNeeded: boolean;
        status: 'sustainable' | 'deficit' | 'future_deficit';
    };
    recommendedFocus: RecommendedFocus;
    strategyRelevance: StrategyRelevance;
    irrelevantStrategies: string[];
    recommendedStrategies: string[];
}

export interface BaselineOpportunitiesResult {
    pensionOpportunity: PensionOptimizationOpportunity;
    contributionOpportunity: ContributionOptimizationOpportunity;
    cashflowOpportunity: CashflowOpportunity;
    recommendedFocus: RecommendedFocus;
    problemStatement: ProblemStatement;
    baselineFinancials: Record<string, unknown>;
    baselineContributions: SupportAtHomeContributions;
    baselineProjections: SupportAtHomeProjectionYear[];
    servicePlanCosts: { isEstimated: boolean; estimatedDescription: string | null | undefined };
    servicePlanResolution: ResolvedServicePlan;
}

/**
 * Analyze baseline opportunities and generate problem statement
 */
export async function analyzeBaselineOpportunities(
    factFind: DraftFactFind,
    servicePlan: SupportAtHomeServicePlan | null = null,
    legacyYears = 5,
    evaluationOptions: Record<string, unknown> = {},
): Promise<BaselineOpportunitiesResult> {
    // 1-4. Baseline financials/contributions/projections now computed server-side in one request
    // (server/src/rest/calculation/suggestionEngine/supportAtHome.ts's computeSupportAtHomeBaselineData,
    // which also feeds evaluateSupportAtHomeStrategies) — everything below (pension/contribution/
    // cashflow-opportunity analysis) is pure and stays local.
    const { baselineFinancials, baselineContributions, baselineProjections } = await calculationApi.computeSupportAtHomeBaselineData(
        factFind,
        (servicePlan || {}) as unknown as Record<string, unknown>,
        legacyYears,
        evaluationOptions
    );

    const isPartnered = factFind?.adviceFor === 'couple';
    const servicePlanResolution = resolveServicePlanForSuggestionEngine(servicePlan, isPartnered);

    // servicePlanCosts here is display-only (isEstimated/estimatedDescription for the UI) — the
    // dollar figures actually used for baselineProjections were already computed server-side above.
    const servicePlanCosts = {
        isEstimated: servicePlanResolution.isEstimated,
        estimatedDescription: servicePlanResolution.description,
    };

    // 5. Analyze pension optimization potential
    const pensionOpportunity = determinePensionOptimizationPotential(
        baselineFinancials,
        factFind
    );

    // 6. Analyze contribution rate optimization potential
    const contributionOpportunity = determineContributionOptimizationPotential(
        baselineContributions,
        pensionOpportunity,
        factFind
    );

    // 7. Analyze cashflow position
    const cashflowOpportunity = analyzeCashflowPosition(baselineProjections);

    // 8. Determine recommended focus
    const recommendedFocus = determineRecommendedFocus(
        pensionOpportunity,
        contributionOpportunity,
        cashflowOpportunity
    );

    // 9. Generate problem statement
    const problemStatement = generateProblemStatement(
        pensionOpportunity,
        contributionOpportunity,
        cashflowOpportunity,
        recommendedFocus
    );

    return {
        pensionOpportunity,
        contributionOpportunity,
        cashflowOpportunity,
        recommendedFocus,
        problemStatement,
        baselineFinancials,
        baselineContributions,
        baselineProjections,
        servicePlanCosts,
        servicePlanResolution,
    };
}

/**
 * Determine pension optimization potential
 */
function determinePensionOptimizationPotential(financials: Record<string, unknown>, factFind: DraftFactFind): PensionOptimizationOpportunity {
    const isPartnered = factFind?.adviceFor === 'couple';
    const isHomeowner = anyoneOwnsHome(factFind as unknown as FactFind);

    const maxPensionRate = isPartnered ? PENSION_RATES.couple : PENSION_RATES.single;
    const agePension = Number(financials.socialSecurity) || 0;
    const pensionRatio = maxPensionRate > 0 ? agePension / maxPensionRate : 0;

    // Determine current pension status
    let pensionStatus: PensionOptimizationOpportunity['status'];
    if (pensionRatio >= 0.95) {
        pensionStatus = 'full';
    } else if (pensionRatio > 0) {
        pensionStatus = 'part';
    } else {
        pensionStatus = 'self-funded';
    }

    // Calculate distance from full pension threshold
    const assetThreshold = isPartnered
        ? (isHomeowner ? ASSET_TEST_LIMITS.couple.homeowner.full : ASSET_TEST_LIMITS.couple.nonHomeowner.full)
        : (isHomeowner ? ASSET_TEST_LIMITS.single.homeowner.full : ASSET_TEST_LIMITS.single.nonHomeowner.full);

    const assessableAssets = Number(financials.assessableAssets) || 0;
    const assetsAboveThreshold = Math.max(0, assessableAssets - assetThreshold);

    // Estimate potential pension gain (simplified - assumes asset reduction directly translates to pension increase)
    // Actual calculation would need to account for both asset and income tests
    let estimatedMaxPensionGain = 0;
    if (pensionStatus !== 'full' && assetsAboveThreshold > 0) {
        // Each $1,000 of assets above threshold reduces pension by $78/year
        // So reducing assets by $1,000 increases pension by $78/year (up to full pension)
        const maxReductionPossible = Math.min(assetsAboveThreshold, assessableAssets);
        const estimatedGainFromAssetReduction = (maxReductionPossible / 1000) * ANNUAL_REDUCTION_PER_1000_ASSETS;
        estimatedMaxPensionGain = Math.min(estimatedGainFromAssetReduction, maxPensionRate - agePension);
    }

    return {
        status: pensionStatus,
        currentPension: agePension,
        maxPension: maxPensionRate,
        pensionRatio,
        canImprovePension: pensionStatus !== 'full' && assetsAboveThreshold > 0,
        assetsAboveThreshold,
        estimatedMaxPensionGain,
        assessableAssets,
        assetThreshold
    };
}

/**
 * Determine contribution rate optimization potential
 */
function determineContributionOptimizationPotential(
    contributions: SupportAtHomeContributions,
    pensionOpportunity: PensionOptimizationOpportunity,
    factFind: DraftFactFind,
): ContributionOptimizationOpportunity {
    const isPartnered = factFind?.adviceFor === 'couple';
    const contributionRates = isPartnered
        ? (contributions?.combined?.contributionRates || {})
        : (contributions?.client?.contributionRates || {});

    const annualContribution = getAnnualContributionAmount(contributions, isPartnered);

    // If full pension, contribution rates are already at minimum (except NWOP Category B)
    // If part/self-funded, can potentially reduce rates via pension increase
    const canReduceViaPension = pensionOpportunity.canImprovePension;

    // Current rates
    const clinicalRate = contributionRates.clinical || 0;
    const independenceRate = contributionRates.independence || 0;
    const everydayLivingRate = contributionRates.everydayLiving || 0;

    // Are rates already at minimum?
    const ratesAtMinimum = clinicalRate === 0 &&
        (independenceRate <= 0.05 || independenceRate === 0) &&
        (everydayLivingRate <= 0.175 || everydayLivingRate === 0);

    return {
        currentRates: {
            clinical: clinicalRate,
            independence: independenceRate,
            everydayLiving: everydayLivingRate
        },
        annualContribution,
        canReduceViaPension,
        ratesAtMinimum,
        optimizationPotential: canReduceViaPension && !ratesAtMinimum ? 'high' :
            (ratesAtMinimum ? 'low' : 'medium')
    };
}

/**
 * Analyze cashflow position from projections
 */
function analyzeCashflowPosition(projections: SupportAtHomeProjectionYear[] | null | undefined): CashflowOpportunity {
    if (!projections || projections.length === 0) {
        return {
            year1Cashflow: 0,
            yearsToDeficit: null,
            cashflowOptimizationNeeded: false,
            averageCashflow: 0
        };
    }

    /*
     * The STANDING cashflow, on the same basis as the starting position above it (review, 18 Oct 2026:
     * that said $1,058 before care less a $13,444 share, −$12,386, while this tile said −$11,909). The
     * row's own breakdown is the year at its opening rates; the row's total is what the year actually
     * paid, which moves as the means test is re-run. "Where you stand" is the first, so it is read from
     * the breakdown when the row has one. The deficit year below still reads the paid totals.
     */
    const openingLines = Array.isArray((projections[0] as { cashFlowBreakdown?: unknown })?.cashFlowBreakdown)
        ? ((projections[0] as { cashFlowBreakdown: Array<{ value?: unknown; note?: boolean }> }).cashFlowBreakdown)
        : null;
    const openingSum = openingLines
        ? openingLines.reduce((t, l) => (l?.note || !Number.isFinite(Number(l?.value)) ? t : t + Number(l.value)), 0)
        : null;
    const year1Cashflow = openingSum != null && openingLines!.length > 0 ? openingSum : getProjectionCashFlow(projections[0]);

    // Find when cashflow goes negative (if at all)
    let yearsToDeficit: number | null = null;
    for (let i = 0; i < projections.length; i++) {
        if (getProjectionCashFlow(projections[i]) < 0) {
            yearsToDeficit = i + 1;
            break;
        }
    }

    const totalCashflow = projections.reduce((sum, p) => sum + getProjectionCashFlow(p), 0);
    const averageCashflow = projections.length > 0 ? totalCashflow / projections.length : 0;

    // Cashflow optimization needed if:
    // - Year 1 is negative, OR
    // - Will go negative within 5 years
    const cashflowOptimizationNeeded = year1Cashflow < 0 || (yearsToDeficit !== null && yearsToDeficit <= 5);

    return {
        year1Cashflow,
        yearsToDeficit,
        cashflowOptimizationNeeded,
        averageCashflow,
        allYearsPositive: yearsToDeficit === null
    };
}

/**
 * Determine recommended focus based on opportunities
 */
function determineRecommendedFocus(
    pensionOpportunity: PensionOptimizationOpportunity,
    contributionOpportunity: ContributionOptimizationOpportunity,
    cashflowOpportunity: CashflowOpportunity,
): RecommendedFocus {
    // If pension can be improved and contribution rates are high, focus on pension optimization
    if (pensionOpportunity.canImprovePension && contributionOpportunity.optimizationPotential === 'high') {
        return 'pension_and_contributions';
    }

    // If pension can be improved but rates are already low, focus on pension + cashflow
    if (pensionOpportunity.canImprovePension && contributionOpportunity.ratesAtMinimum) {
        return 'pension_and_cashflow';
    }

    // If pension is already maximized but cashflow is poor, focus on cashflow
    if (!pensionOpportunity.canImprovePension && cashflowOpportunity.cashflowOptimizationNeeded) {
        return 'cashflow';
    }

    // If pension is maximized and cashflow is good, focus on legacy preservation
    if (!pensionOpportunity.canImprovePension && !cashflowOpportunity.cashflowOptimizationNeeded) {
        return 'legacy';
    }

    // Default: balanced approach
    return 'balanced';
}

/**
 * Generate problem statement with strategy relevance
 */
function generateProblemStatement(
    pensionOpportunity: PensionOptimizationOpportunity,
    contributionOpportunity: ContributionOptimizationOpportunity,
    cashflowOpportunity: CashflowOpportunity,
    recommendedFocus: RecommendedFocus,
): ProblemStatement {
    const pensionStatus = pensionOpportunity.status;

    // Build summary
    let summary = '';
    if (pensionStatus === 'full') {
        summary = "You are currently receiving full Age Pension. Strategies focused on pension optimization (gifting, funeral bonds to increase pension) will not provide additional pension benefit. ";
        if (cashflowOpportunity.cashflowOptimizationNeeded) {
            summary += "We recommend focusing on cashflow optimization and legacy preservation strategies.";
        } else {
            summary += "We recommend focusing on legacy preservation and maintaining sustainable cashflow.";
        }
    } else if (pensionStatus === 'part') {
        // Review, 18 Oct 2026: "could rise by approximately $35,958" beside a best plan worth $19,500, with
        // neither figure explained. The figure is a CEILING — the pension if counted assets fell all the
        // way to the full-pension limit — so it is called that, and the plans say what they really add.
        summary = `You receive a part Age Pension. If the assets Centrelink counts came down to the full-pension limit, it would be about $${Math.round(pensionOpportunity.estimatedMaxPensionGain).toLocaleString()} a year higher. That is the most it could rise, not what any one plan achieves; each plan below shows what it really adds. Counting fewer assets can also lower the Support at Home contribution rates.`;
    } else {
        summary = "You are currently self-funded (not receiving Age Pension). There is significant potential to qualify for Age Pension and reduce your Support at Home contribution rates through asset restructuring strategies.";
    }

    // Determine strategy relevance
    const strategyRelevance: StrategyRelevance = {
        gifting: {
            relevant: pensionStatus !== 'full' || contributionOpportunity.optimizationPotential !== 'low',
            reason: pensionStatus === 'full'
                ? "Will not increase pension (already full), but may still help with contribution rate optimization if you're in NWOP Category B"
                : "Can reduce assessable assets and potentially increase Age Pension entitlement"
        },
        funeralBonds: {
            relevant: pensionStatus !== 'full' || contributionOpportunity.optimizationPotential !== 'low',
            reason: pensionStatus === 'full'
                ? "Will not increase pension (already full), but may still be beneficial for funeral planning"
                : `Can reduce assessable assets (up to ${formatCurrency(FUNERAL_BOND_LIMIT)} per person exempt) and potentially increase Age Pension`
        },
        annuities: {
            relevant: true, // Always relevant - can help with pension, cashflow, and legacy
            reason: pensionStatus === 'full'
                ? "Can provide guaranteed income to improve cashflow and preserve legacy value"
                : "Can reduce assessable assets (60% or 30% assessment) to increase pension, plus provide guaranteed income"
        },
        propertyStrategies: {
            relevant: true, // Always relevant - multiple benefits
            reason: "Can optimize asset allocation, improve cashflow, and preserve estate value"
        },
        agedCareIntegration: {
            relevant: true, // Always relevant for planning
            reason: "Helps prepare for potential future transition to residential aged care"
        }
    };

    // Identify irrelevant strategies
    const irrelevantStrategies: string[] = [];
    if (!strategyRelevance.gifting.relevant) irrelevantStrategies.push('gifting');
    if (!strategyRelevance.funeralBonds.relevant) irrelevantStrategies.push('funeralBonds');

    // Recommended strategies based on focus
    const recommendedStrategies: string[] = [];
    if (recommendedFocus === 'pension_and_contributions' || recommendedFocus === 'pension_and_cashflow') {
        if (strategyRelevance.gifting.relevant) recommendedStrategies.push('gifting');
        if (strategyRelevance.funeralBonds.relevant) recommendedStrategies.push('funeralBonds');
        recommendedStrategies.push('annuities');
    }
    if (recommendedFocus === 'cashflow' || recommendedFocus === 'legacy' || recommendedFocus === 'balanced') {
        recommendedStrategies.push('annuities');
        recommendedStrategies.push('propertyStrategies');
    }

    return {
        summary,
        pensionOptimization: {
            status: pensionStatus,
            canImprove: pensionOpportunity.canImprovePension,
            currentPension: pensionOpportunity.currentPension,
            maxPension: pensionOpportunity.maxPension,
            estimatedMaxGain: pensionOpportunity.estimatedMaxPensionGain,
            reason: pensionStatus === 'full'
                ? "Already receiving maximum Age Pension entitlement"
                : `Can potentially increase pension by up to $${Math.round(pensionOpportunity.estimatedMaxPensionGain).toLocaleString()} per year`,
            recommendation: pensionStatus === 'full'
                ? "Focus on cashflow/legacy instead"
                : "Asset restructuring strategies can help increase pension"
        },
        contributionOptimization: {
            currentRates: contributionOpportunity.currentRates,
            annualContribution: contributionOpportunity.annualContribution,
            canReduceViaPension: contributionOpportunity.canReduceViaPension,
            ratesAtMinimum: contributionOpportunity.ratesAtMinimum,
            optimizationPotential: contributionOpportunity.optimizationPotential
        },
        cashflowPosition: {
            year1Cashflow: cashflowOpportunity.year1Cashflow,
            yearsToDeficit: cashflowOpportunity.yearsToDeficit,
            optimizationNeeded: cashflowOpportunity.cashflowOptimizationNeeded,
            status: cashflowOpportunity.allYearsPositive ? 'sustainable' :
                (cashflowOpportunity.year1Cashflow < 0 ? 'deficit' : 'future_deficit')
        },
        recommendedFocus,
        strategyRelevance,
        irrelevantStrategies,
        recommendedStrategies
    };
}

export interface EnabledStrategiesSettings {
    includeGifting?: boolean;
    includeFuneralBonds?: boolean;
    includeAnnuities?: boolean;
    includePropertyStrategies?: boolean;
    includeAgedCareIntegration?: boolean;
    [key: string]: unknown;
}

export interface IrrelevantStrategyEntry {
    strategy: string;
    reason: string;
    suggested: string[];
}

export interface StrategyRelevanceWarning {
    strategy: string;
    message: string;
    severity: 'info' | 'warning' | 'error';
}

export interface FilteredStrategiesResult {
    toSkip: IrrelevantStrategyEntry[];
    toKeep: string[];
    warnings: StrategyRelevanceWarning[];
}

/**
 * Identify which enabled strategies should be filtered out
 */
export function identifyIrrelevantStrategies(problemStatement: ProblemStatement, enabledStrategies: EnabledStrategiesSettings): FilteredStrategiesResult {
    const filtered: FilteredStrategiesResult = {
        toSkip: [],
        toKeep: [],
        warnings: []
    };

    // Check each enabled strategy
    if (enabledStrategies.includeGifting && !problemStatement.strategyRelevance.gifting.relevant) {
        filtered.toSkip.push({
            strategy: 'gifting',
            reason: problemStatement.strategyRelevance.gifting.reason,
            suggested: problemStatement.recommendedStrategies
        });
        filtered.warnings.push({
            strategy: 'Gifting to Family',
            message: problemStatement.strategyRelevance.gifting.reason,
            severity: 'info'
        });
    } else if (enabledStrategies.includeGifting) {
        filtered.toKeep.push('gifting');
    }

    if (enabledStrategies.includeFuneralBonds && !problemStatement.strategyRelevance.funeralBonds.relevant) {
        filtered.toSkip.push({
            strategy: 'funeralBonds',
            reason: problemStatement.strategyRelevance.funeralBonds.reason,
            suggested: problemStatement.recommendedStrategies
        });
        filtered.warnings.push({
            strategy: 'Funeral Bonds',
            message: problemStatement.strategyRelevance.funeralBonds.reason,
            severity: 'info'
        });
    } else if (enabledStrategies.includeFuneralBonds) {
        filtered.toKeep.push('funeralBonds');
    }

    if (enabledStrategies.includeAnnuities) {
        filtered.toKeep.push('annuities');
    }

    if (enabledStrategies.includePropertyStrategies) {
        filtered.toKeep.push('propertyStrategies');
    }

    if (enabledStrategies.includeAgedCareIntegration) {
        filtered.toKeep.push('agedCareIntegration');
    }

    return filtered;
}
